import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';
import { initWebPush, teardownWebPush } from '../services/pushNotifications';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    if (token) {
      loadUser();
    } else {
      setLoading(false);
    }
  }, []);

  const loadUser = async () => {
    try {
      setLoading(true);
      const response = await authAPI.getCurrentUser();
      if (response.success) {
        setUser(response.data);
        // Re-register web push in case the service worker was cleared
        initWebPush().catch((err) => console.error('[push] initWebPush failed:', err));
      }
    } catch (err) {
      console.error('Failed to load user:', err);
      localStorage.removeItem('token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await authAPI.login({ email, password });
      
      if (response.success) {
        localStorage.setItem('token', response.data.token);
        setUser(response.data);
        // Register for web push notifications (non-blocking)
        initWebPush().catch((err) => console.error('[push] initWebPush failed:', err));
        return { success: true };
      } else {
        throw new Error(response.error?.message || 'Login failed');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error?.message || err.message || 'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      const response = await authAPI.register(userData);
      
      if (response.success) {
        // Check if user is pending approval (no token returned)
        if (!response.data.token && response.data.status === 'pending') {
          // Registration successful but pending approval
          return { 
            success: true, 
            pending: true,
            message: 'Registration successful! Your account is pending admin approval. You will receive an email notification once approved.'
          };
        }
        
        // User approved immediately - set token and user
        localStorage.setItem('token', response.data.token);
        setUser(response.data);
        return { success: true };
      } else {
        throw new Error(response.error?.message || 'Registration failed');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error?.message || err.message || 'Registration failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    // Unsubscribe from web push before clearing state
    teardownWebPush().catch((err) => console.error('[push] teardownWebPush failed:', err));
    localStorage.removeItem('token');
    setUser(null);
    setError(null);
  };

  const updateProfile = async (payload) => {
    try {
      const response = await authAPI.updateProfile(payload);
      if (response.success) {
        setUser(response.data);
        return { success: true };
      }
      return { success: false, error: response.error?.message || 'Update failed' };
    } catch (err) {
      const msg = err.response?.data?.error?.message || err.message || 'Update failed';
      return { success: false, error: msg };
    }
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return (user.permissions || []).includes(permission);
  };

  const isElevated = user &&
    (user.role === 'admin' || user.role === 'manager' || user.role === 'contributor');

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    updateProfile,
    hasPermission,
    isElevated: !!isElevated,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
